import * as Y from 'yjs'

/**
 * 새로운 Path를 Yjs Map에 추가
 * @param {Y.Doc} ydoc - Yjs 문서 객체
 * @param {Y.Map} yPaths - Path들을 저장하는 Yjs Map
 * @param {string} pathId - 추가할 Path의 고유 ID
 * @param {object} pathObj - 추가할 Path 데이터 객체
 */
export const addPath = (ydoc, yPaths, pathId, pathObj) => {
  const yMap = new Y.Map()

  ydoc.transact(() => {
    for (const [key, value] of Object.entries(pathObj)) {
      if (key === 'points') {
        const yArr = new Y.Array()
        yArr.push([...value])
        yMap.set('points', yArr)
      } else {
        yMap.set(key, value)
      }
    }
    yPaths.set(pathId, yMap)
  })
}

/**
 * 기존 Path의 포인트를 추가
 * @param {Y.Map} yPaths - Path들을 저장하는 Yjs Map
 * @param {string} pathId - 수정할 Path의 고유 ID
 * @param {object} pointObj - 추가할 포인트 객체
 */
export const appendPoint = (yPaths, pathId, pointObj) => {
  const path = yPaths.get(pathId)
  if (!path) return

  const yPoints = path.get('points')
  if (!(yPoints instanceof Y.Array)) return

  yPoints.push([pointObj])
}

/**
 * 기존 Path의 포인트 배열을 완전히 교체
 * @param {Y.Doc} ydoc - Yjs 문서 객체
 * @param {Y.Map} yPaths - Path들을 저장하는 Yjs Map
 * @param {string} pathId - 수정할 Path의 고유 ID
 * @param {Array<object>} newPoints - 새로 교체할 포인트 배열
 */
export const replacePoints = (ydoc, yPaths, pathId, newPoints) => {
  const path = yPaths.get(pathId)
  if (!path) return

  const yPoints = path.get('points')
  if (!(yPoints instanceof Y.Array)) return

  ydoc.transact(() => {
    yPoints.delete(0, yPoints.length) // 기존 포인트 전체 삭제
    yPoints.push([...newPoints]) // 새 배열 한 번에 push
  })
}

/**
 * 기존 Path의 속성 및 포인트를 업데이트
 * @param {Y.Doc} ydoc - Yjs 문서 객체
 * @param {Y.Map} yPaths - Path들을 저장하는 Yjs Map
 * @param {string} pathId - 수정할 Path의 고유 ID
 * @param {object} updates - 업데이트할 속성 객체
 * @param {'append' | 'replace' | 'no-op'} pointUpdateType - 포인트 업데이트 방식
 * @param {object | null} appendPointObj - 'append' 시 추가할 포인트 객체
 */
export const updatePath = (ydoc, yPaths, pathId, updates, pointUpdateType, appendPointObj) => {
  const path = yPaths.get(pathId)
  if (!path) return

  ydoc.transact(() => {
    // 1. 일반 속성 업데이트
    for (const [key, value] of Object.entries(updates)) {
      if (key !== 'points') {
        path.set(key, value)
      }
    }

    // 2. points 처리
    switch (pointUpdateType) {
      case 'append':
        if (appendPointObj) {
          appendPoint(yPaths, pathId, appendPointObj)
        }
        break
      case 'replace':
        if (updates.points) {
          replacePoints(ydoc, yPaths, pathId, updates.points)
        }
        break
      case 'no-op':
        break
      default:
        console.warn('[Yjs] Invalid pointUpdateType:', pointUpdateType)
    }
  })
}

/**
 * Translate와 Scope 상태를 Yjs로 동기화
 * @param {Y.Doc} ydoc - Yjs 문서 객체
 * @param {Y.Map} yTranslate - Translate 정보를 담는 Yjs Map
 * @param {Y.Map} yScope - Scope 정보를 담는 Yjs Map
 * @param {{x: number, y: number}} newTranslate - 새로운 translate 값
 * @param {number} newScope - 새로운 scope 값
 * @param {number} scaleRatio - 디바이스별 스케일 정규화를 위한 비율
 */
export const updateTranslateAndScope = (
  ydoc,
  yTranslate,
  yScope,
  newTranslate,
  newScope,
  scaleRatio
) => {
  ydoc.transact(() => {
    // [ TRANSLATE ]
    yTranslate.set('translateX', newTranslate.x)
    yTranslate.set('translateY', newTranslate.y)

    // [ SCOPE ]
    // newScope / scaleRatio : 디바이스마다 scaleRatio가 다르니까, 기준 단위로 정규화해서 저장
    yScope.set('scope', newScope / scaleRatio)
    // ydocId : yjs에서 자기 자신에 대한 동기화는 수신하지 않기 위해 저장
    yScope.set('ydocId', ydoc.clientID)
  })
}
